import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sun, Moon, Menu, X, ChevronDown, User, LogOut, Shield, Plus, Layers, Bookmark } from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  const navLinks = [
    { label: 'Browse', href: '/' },
    { label: 'Categories', href: '/categories' },
    { label: 'Platforms', href: '/platforms' },
    { label: 'LLM Costs', href: '/LLM-Cost-Calculator' },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Navbar */}
      <header className="backdrop-blur-xl bg-background/80 border-b border-border/50 sticky top-0 z-50" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
              <img src="/logo.png" alt="PromptVault" className="w-8 h-8 rounded-lg object-contain" />
              <span className="text-lg font-bold tracking-tight">PromptVault</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.href) ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {user ? (
                <>
                  <Link to="/submit" className="hidden sm:inline-flex">
                    <Button size="sm" className="rounded-full px-4 gap-1.5 shadow-lg shadow-primary/20" data-testid="submit-prompt-btn">
                      <Plus className="w-3.5 h-3.5" /> Share Prompt
                    </Button>
                  </Link>
                  <Link to="/submit-platform" className="hidden sm:inline-flex">
                    <Button size="sm" variant="outline" className="rounded-full px-4 gap-1.5 border-emerald-500 text-emerald-500 bg-transparent hover:bg-emerald-500/10" data-testid="submit-platform-btn">
                      <Plus className="w-3.5 h-3.5" /> Submit Platform
                    </Button>
                  </Link>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="rounded-full gap-2 pl-2 pr-3" data-testid="user-menu-trigger">
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                          {user.username?.charAt(0).toUpperCase()}
                        </div>
                        <span className="hidden sm:inline text-sm font-medium">{user.username}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="menu-profile">
                        <User className="w-4 h-4 mr-2" /> Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/submit')} className="sm:hidden" data-testid="menu-submit-mobile">
                        <Plus className="w-4 h-4 mr-2" /> Share Prompt
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/submit-platform')} data-testid="menu-submit-platform">
                        <Layers className="w-4 h-4 mr-2" /> Submit Platform
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/bookmarks')} data-testid="menu-bookmarks">
                        <Bookmark className="w-4 h-4 mr-2" /> Saved Prompts
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                            <Shield className="w-4 h-4 mr-2" /> Admin Panel
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" /> Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="rounded-full" data-testid="login-btn">Log in</Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="rounded-full px-4 shadow-lg shadow-primary/20" data-testid="register-btn">Sign up</Button>
                  </Link>
                </div>
              )}

              {/* Mobile toggle */}
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileOpen && (
            <div className="md:hidden pb-4 border-t border-border/50 pt-3 space-y-1">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive(link.href) ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/50 backdrop-blur-sm" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="PromptVault" className="w-6 h-6 rounded object-contain" />
              <span className="text-sm font-semibold">PromptVault</span>
              <span className="text-xs text-muted-foreground">&copy; 2026 PromptVault&trade; a service of Concepts.io LLC</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Community-driven AI prompt library. Share, discover, and upvote the best prompts.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
